import { postJson } from "@/domain/common/client/ApiClient"
import { toDetail, toSummary } from "@/domain/entry/client/EntryClient"
import { entryPlayerStatusPayloadFromJSON } from "@bracket/idl/main/entry/model/EntryPt"
import { entryStatusPayloadFromJSON } from "@bracket/idl/main/entry/model/EntryPt"
import {
  EntryAdminGetByIdRequestPayload,
  EntryAdminGetByIdResponsePayload,
  EntryAdminGetByPagingRequestPayload,
  EntryAdminGetByPagingResponsePayload,
  EntryClassifyWeightClassRequestPayload,
  EntryGetWeightClassCandidateRequestPayload,
  EntryGetWeightClassCandidateResponsePayload,
  EntryResetPasswordRequestPayload,
  EntryResetPasswordResponsePayload,
  EntryUpdatePlayerStatusRequestPayload,
} from "@bracket/idl/main/entry/payload/EntryAdminPayloadPt"
import { EmptyResponsePayload } from "@bracket/idl/main/common/payload/EmptyRequestPayloadPt"
import { PagingRequestPayload } from "@bracket/idl/main/common/model/PagingPt"
import { fightSportPayloadFromJSON } from "@bracket/idl/main/fight/model/enums/FightSportPt"
import { sportLabel } from "@/domain/fight/model/Fight"
import type { WeightClassPayload } from "@bracket/idl/main/fight/model/FightPt"
import type {
  AdminEntrySearchInput,
  WeightClassOption,
} from "@/domain/admin/model/AdminEntry"
import type { EntryDetail, EntrySummary } from "@/domain/entry/model/Entry"

interface AdminEntryPage {
  items: EntrySummary[]
  totalCount: number
}

interface AdminEntryDetailResult {
  entry: EntryDetail
  tournamentName: string
}

interface WeightClassCandidateResult {
  candidates: WeightClassOption[]
  allOfSport: WeightClassOption[]
}

interface AdminEntryPagingParam {
  search: AdminEntrySearchInput
  skip: number
  take: number
}

function toSearchPayload(search: AdminEntrySearchInput) {
  return {
    tournamentId: search.tournamentId,
    gymName: search.gymName,
    playerName: search.playerName,
    status: entryStatusPayloadFromJSON(search.status || "ENTRY_STATUS_UNKNOWN"),
    sport: fightSportPayloadFromJSON(search.sport || "FIGHT_SPORT_UNKNOWN"),
  }
}

function toWeightClassOption(payload: WeightClassPayload): WeightClassOption {
  return {
    key: `${sportLabel(payload.sport)}-${payload.name}`,
    name: payload.name,
    payload,
  }
}

/**
 * 참가 신청 관리 클라이언트 (관리자).
 *
 * 경로는 fight-bracket-server 의 `EntryAdminRouter` 와 1:1 대응한다.
 * 체급은 이 클라이언트의 `classifyWeightClass` 만 채울 수 있다 — 신청 · 수정에서는 서버가 무시한다.
 */
const AdminEntryClient = {
  async getByPaging(param: AdminEntryPagingParam): Promise<AdminEntryPage> {
    const request = EntryAdminGetByPagingRequestPayload.create({
      paging: PagingRequestPayload.create({
        skip: param.skip,
        take: param.take,
      }),
      search: toSearchPayload(param.search),
    })

    const json = await postJson<unknown>(
      "/bo/entry/get-by-paging",
      EntryAdminGetByPagingRequestPayload.toJSON(request),
    )
    const response = EntryAdminGetByPagingResponsePayload.fromJSON(json)

    return {
      items: response.data.map(toSummary),
      totalCount: response.paging?.totalCount ?? 0,
    }
  },

  async getById(id: string): Promise<AdminEntryDetailResult> {
    const request = EntryAdminGetByIdRequestPayload.create({ id })

    const json = await postJson<unknown>(
      "/bo/entry/get-by-id",
      EntryAdminGetByIdRequestPayload.toJSON(request),
    )
    const response = EntryAdminGetByIdResponsePayload.fromJSON(json)

    if (!response.data) throw new Error("신청 내역을 찾을 수 없습니다.")

    return {
      entry: toDetail(
        response.data,
        response.tournament?.name ?? "",
        response.tournament?.startAt ?? "",
      ),
      tournamentName: response.tournament?.name ?? "",
    }
  },

  /** 체급 후보 — 0개면 그 부의 체급표가 없다는 뜻이라 종목 전체에서 고른다 */
  async getWeightClassCandidate(
    playerId: string,
  ): Promise<WeightClassCandidateResult> {
    const request = EntryGetWeightClassCandidateRequestPayload.create({
      playerId,
    })

    const json = await postJson<unknown>(
      "/bo/entry/get-weight-class-candidate",
      EntryGetWeightClassCandidateRequestPayload.toJSON(request),
    )
    const response = EntryGetWeightClassCandidateResponsePayload.fromJSON(json)

    return {
      candidates: response.candidate.map(toWeightClassOption),
      allOfSport: response.allOfSport.map(toWeightClassOption),
    }
  },

  async classifyWeightClass(
    playerId: string,
    weightClass: WeightClassPayload,
  ): Promise<void> {
    const request = EntryClassifyWeightClassRequestPayload.create({
      playerId,
      weightClass,
    })

    const json = await postJson<unknown>(
      "/bo/entry/classify-weight-class",
      EntryClassifyWeightClassRequestPayload.toJSON(request),
    )
    EmptyResponsePayload.fromJSON(json)
  },

  async updatePlayerStatus(
    playerId: string,
    status: string,
    rejectedReason = "",
  ): Promise<void> {
    const request = EntryUpdatePlayerStatusRequestPayload.create({
      playerId,
      status: entryPlayerStatusPayloadFromJSON(status),
      rejectedReason,
    })

    const json = await postJson<unknown>(
      "/bo/entry/update-player-status",
      EntryUpdatePlayerStatusRequestPayload.toJSON(request),
    )
    EmptyResponsePayload.fromJSON(json)
  },


  /** 비밀번호 초기화 — 발급된 임시 비밀번호는 이 응답에만 담긴다 */
  async resetPassword(id: string): Promise<string> {
    const request = EntryResetPasswordRequestPayload.create({ id })

    const json = await postJson<unknown>(
      "/bo/entry/reset-password",
      EntryResetPasswordRequestPayload.toJSON(request),
    )
    const response = EntryResetPasswordResponsePayload.fromJSON(json)

    return response.temporaryPassword
  },
}

export { AdminEntryClient, toWeightClassOption }
export type {
  AdminEntryDetailResult,
  AdminEntryPage,
  AdminEntryPagingParam,
  WeightClassCandidateResult,
}
